import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, TrendingDown, CalendarClock, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const TABS = [
  { key: "low", label: "Low Stock", icon: AlertTriangle },
  { key: "shortage", label: "Shortage", icon: TrendingDown },
  { key: "expiry", label: "Expiry", icon: CalendarClock },
];

const csvDownload = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map(r => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

function FilterBar({ f, setF, companies, categories, groups, showDates = false, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 border border-border bg-card p-3 rounded-sm print:hidden items-end">
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
        <Select value={f.company} onValueChange={(v) => setF(x => ({ ...x, company: v }))}>
          <SelectTrigger data-testid={`${prefix}-filter-company`} className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
        <Select value={f.category} onValueChange={(v) => setF(x => ({ ...x, category: v }))}>
          <SelectTrigger data-testid={`${prefix}-filter-category`} className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
        <Select value={f.group} onValueChange={(v) => setF(x => ({ ...x, group: v }))}>
          <SelectTrigger data-testid={`${prefix}-filter-group`} className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {showDates && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From Date</Label>
            <Input data-testid={`${prefix}-date-from`} type="date" value={f.date_from} onChange={(e) => setF(x => ({ ...x, date_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To Date</Label>
            <Input data-testid={`${prefix}-date-to`} type="date" value={f.date_to} onChange={(e) => setF(x => ({ ...x, date_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
          </div>
        </>
      )}
    </div>
  );
}

function TabActions({ onExport, prefix }) {
  return (
    <div className="flex gap-2 print:hidden">
      <Button data-testid={`${prefix}-export`} variant="outline" onClick={onExport} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export CSV</Button>
      <Button data-testid={`${prefix}-print`} variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
    </div>
  );
}

function LowStockTab({ companies, categories, groups }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ company: "all", category: "all", group: "all" });
  useEffect(() => { api.get("/stocks/low-stock").then(r => setRows(r.data)).catch(e => toast.error(formatErr(e))); }, []);
  const filtered = useMemo(() => rows.filter(r =>
    (f.company === "all" || r.company === f.company) &&
    (f.category === "all" || r.category === f.category) &&
    (f.group === "all" || r.group === f.group)
  ), [rows, f]);
  const pg = usePagination(filtered);
  const critical = filtered.filter(r => r.critical).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">Items at or below their reorder level</div>
        <TabActions prefix="low-stock" onExport={() => csvDownload("low_stock.csv",
          ["Item Name", "Part No", "Category", "Current Stock", "Reorder Level", "Unit", "Supplier"],
          filtered.map(r => [r.item_name, r.part_number, r.category, r.current_stock, r.reorder_level, r.unit, r.supplier]))}/>
      </div>
      <FilterBar f={f} setF={setF} companies={companies} categories={categories} groups={groups} prefix="low-stock"/>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs md:max-w-md print:hidden">
        <div className="border border-border bg-card rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Low Items</div><div className="text-xl font-display font-black" data-testid="low-stock-count">{filtered.length}</div></div>
        <div className="border border-destructive/50 bg-destructive/10 rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Critical (Zero)</div><div className="text-xl font-display font-black text-destructive" data-testid="low-stock-critical">{critical}</div></div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="low-stock-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th><th className="text-left p-3">Part No</th>
              <th className="text-left p-3">Category</th><th className="text-right p-3">Current Stock</th>
              <th className="text-right p-3">Reorder Level</th><th className="text-left p-3">Unit</th>
              <th className="text-left p-3">Supplier</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No low-stock items</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.item_id} data-testid={`low-stock-row-${r.item_id}`} className={`border-t border-border ${r.critical ? "bg-destructive/15" : "hover:bg-secondary/40"}`}>
                <td className="p-3">{r.critical && <AlertTriangle className="w-3 h-3 inline mr-1 text-destructive"/>}{r.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.category}</td>
                <td className={`p-3 text-right font-mono font-semibold ${r.critical ? "text-destructive" : "text-yellow-500"}`}>{r.current_stock}</td>
                <td className="p-3 text-right font-mono">{r.reorder_level}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className="p-3 text-xs">{r.supplier}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}

function ShortageTab({ companies, categories, groups }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ company: "all", category: "all", group: "all", date_from: "", date_to: "" });
  useEffect(() => {
    const p = new URLSearchParams();
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    const t = setTimeout(() => { api.get(`/stocks/shortage?${p}`).then(r => setRows(r.data)).catch(e => toast.error(formatErr(e))); }, 200);
    return () => clearTimeout(t);
  }, [f.date_from, f.date_to]);
  const filtered = useMemo(() => rows.filter(r =>
    (f.company === "all" || r.company === f.company) &&
    (f.category === "all" || r.category === f.category) &&
    (f.group === "all" || r.group === f.group)
  ), [rows, f.company, f.category, f.group]);
  const pg2 = usePagination(filtered);
  const totalShortage = filtered.reduce((a, r) => a + r.shortage, 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">Pending demand vs available stock</div>
        <TabActions prefix="shortage" onExport={() => csvDownload("stock_shortage.csv",
          ["Item Name", "Part No", "Pending Demand", "Available Stock", "Shortage Qty", "Unit"],
          filtered.map(r => [r.item_name, r.part_number, r.pending_demand, r.available, r.shortage, r.unit]))}/>
      </div>
      <FilterBar f={f} setF={setF} companies={companies} categories={categories} groups={groups} showDates prefix="shortage"/>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs md:max-w-md print:hidden">
        <div className="border border-border bg-card rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Short Items</div><div className="text-xl font-display font-black" data-testid="shortage-count">{filtered.length}</div></div>
        <div className="border border-destructive/50 bg-destructive/10 rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Shortage Qty</div><div className="text-xl font-display font-black text-destructive" data-testid="shortage-total">{totalShortage}</div></div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="shortage-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th><th className="text-left p-3">Part No</th>
              <th className="text-right p-3">Pending Demand</th><th className="text-right p-3">Available Stock</th>
              <th className="text-right p-3">Shortage Qty</th><th className="text-left p-3">Unit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground" data-testid="shortage-empty">No shortages — all pending demand can be fulfilled</td></tr>}
            {pg2.pageItems.map(r => (
              <tr key={r.item_id} data-testid={`shortage-row-${r.item_id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3">{r.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number}</td>
                <td className="p-3 text-right font-mono">{r.pending_demand}</td>
                <td className="p-3 text-right font-mono">{r.available}</td>
                <td className="p-3 text-right font-mono font-semibold text-destructive">{r.shortage}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-secondary/50 font-semibold">
                <td colSpan={4} className="p-3 text-right uppercase tracking-wider text-xs">Total Shortage</td>
                <td className="p-3 text-right font-mono text-destructive">{totalShortage}</td>
                <td className="p-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
        <PaginationBar pg={pg2} testid="pagination-2"/>
      </div>
    </div>
  );
}

function ExpiryTab({ companies, categories, groups }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ company: "all", category: "all", group: "all" });
  useEffect(() => { api.get("/stocks/batches?expiring_days=30").then(r => setRows(r.data.rows || [])).catch(e => toast.error(formatErr(e))); }, []);
  const filtered = useMemo(() => rows.filter(r =>
    (f.company === "all" || (r.company || "-") === f.company) &&
    (f.category === "all" || (r.category || "-") === f.category) &&
    (f.group === "all" || (r.group || "-") === f.group)
  ), [rows, f]);
  const pg3 = usePagination(filtered);
  const expired = filtered.filter(r => r.expired).length;
  const soon = filtered.length - expired;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">Batches expired or expiring within 30 days</div>
        <TabActions prefix="expiry" onExport={() => csvDownload("stock_expiry.csv",
          ["Item Name", "Part No", "Batch No", "Expiry Date", "Quantity", "Unit", "Store or Godown", "Status"],
          filtered.map(r => [r.item_name, r.part_number || "-", r.batch_number, r.expiry_date || "-", r.qty_remaining, r.unit, "-", r.expired ? "Expired" : "Expiring Soon"]))}/>
      </div>
      <FilterBar f={f} setF={setF} companies={companies} categories={categories} groups={groups} prefix="expiry"/>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs md:max-w-md print:hidden">
        <div className="border border-destructive/50 bg-destructive/10 rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expired</div><div className="text-xl font-display font-black text-destructive" data-testid="expiry-expired">{expired}</div></div>
        <div className="border border-yellow-500/50 bg-yellow-500/10 rounded-sm p-2 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expiring Soon (≤30d)</div><div className="text-xl font-display font-black text-yellow-500" data-testid="expiry-soon">{soon}</div></div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="expiry-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th><th className="text-left p-3">Part No</th>
              <th className="text-left p-3">Batch No</th><th className="text-left p-3">Expiry Date</th>
              <th className="text-right p-3">Quantity</th><th className="text-left p-3">Unit</th>
              <th className="text-left p-3">Store or Godown</th><th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground" data-testid="expiry-empty">No expired or expiring batches</td></tr>}
            {pg3.pageItems.map(r => (
              <tr key={r.batch_id} data-testid={`expiry-row-${r.batch_id}`} className={`border-t border-border ${r.expired ? "bg-destructive/10" : "bg-yellow-500/5 hover:bg-secondary/40"}`}>
                <td className="p-3">{r.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.part_number || "-"}</td>
                <td className="p-3 font-mono text-xs">{r.batch_number}</td>
                <td className="p-3 text-xs">{r.expiry_date || "—"}</td>
                <td className="p-3 text-right font-mono font-semibold">{r.qty_remaining}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className="p-3 text-xs text-muted-foreground">-</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${r.expired ? "bg-destructive/20 text-destructive" : "bg-yellow-500/20 text-yellow-500"}`}>
                    {r.expired ? "Expired" : "Expiring Soon"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg3} testid="pagination-3"/>
      </div>
    </div>
  );
}

export default function StockStatus() {
  const [tab, setTab] = useState("low");
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 print:p-4">
      <div className="mb-6 print:mb-3">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><AlertTriangle className="w-3 h-3"/> Stocks</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Stock Status</h1>
        <div className="text-xs text-muted-foreground mt-1">Low stock, shortage and expiry overview</div>
      </div>

      <div className="flex gap-2 mb-4 border-b border-border print:hidden">
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="w-3 h-3"/>{t.label}
          </button>
        ))}
      </div>

      {tab === "low" && <LowStockTab companies={companies} categories={categories} groups={groups}/>}
      {tab === "shortage" && <ShortageTab companies={companies} categories={categories} groups={groups}/>}
      {tab === "expiry" && <ExpiryTab companies={companies} categories={categories} groups={groups}/>}
    </div>
  );
}
